import { Menu } from 'lucide-react';
import { useState } from 'react';
import { Outlet } from 'react-router';
import { useSession } from '../../shared/api/session';
import { LiveSocketProvider, useLiveSocket } from '../../shared/socket/live-socket';
import { Button } from '../../shared/ui/Button';
import { cn } from '../../shared/ui/cn';
import { LiveStatus } from '../../shared/ui/LiveStatus';
import { Sidebar } from './Sidebar';

const SIDEBAR_ID = 'main-menu';

/**
 * The signed-in website: the menu on the left (a slide-in panel on phones), a slim top bar with the
 * live-updates indicator, and the page. It holds the one WebSocket every page listens on.
 */
export function AppLayout() {
  const { accessToken } = useSession();
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  return (
    <LiveSocketProvider enabled={Boolean(accessToken)}>
      <div className="min-h-svh lg:pl-64">
        <a
          href="#main"
          className="sr-only z-50 rounded-control bg-surface px-3 py-2 font-semibold focus:not-sr-only focus:fixed focus:top-2 focus:left-2"
        >
          Skip to the page
        </a>

        {menuOpen ? (
          <button
            type="button"
            aria-label="Close the menu"
            className="fixed inset-0 z-30 cursor-default bg-overlay lg:hidden"
            onClick={closeMenu}
          />
        ) : null}
        <div
          id={SIDEBAR_ID}
          className={cn(
            'fixed inset-y-0 left-0 z-40 w-64 transition-transform duration-200',
            // Hidden on phones until opened: invisible also keeps its links out of the Tab order
            menuOpen ? 'translate-x-0' : 'max-lg:invisible max-lg:-translate-x-full',
          )}
        >
          <Sidebar onNavigate={closeMenu} />
        </div>

        <div className="flex min-h-svh flex-col">
          <TopBar menuOpen={menuOpen} onOpenMenu={() => setMenuOpen(true)} />
          <main id="main" className="mx-auto grid w-full max-w-7xl flex-1 content-start px-4 py-6 lg:px-8">
            <Outlet />
          </main>
        </div>
      </div>
    </LiveSocketProvider>
  );
}

function TopBar({ menuOpen, onOpenMenu }: { menuOpen: boolean; onOpenMenu: () => void }) {
  const { connected } = useLiveSocket();
  return (
    <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-line bg-surface/90 px-4 backdrop-blur lg:px-8">
      <Button
        variant="ghost"
        size="sm"
        className="lg:hidden"
        aria-label="Open the menu"
        aria-expanded={menuOpen}
        aria-controls={SIDEBAR_ID}
        onClick={onOpenMenu}
      >
        <Menu aria-hidden="true" className="size-5" />
      </Button>
      <span className="font-bold lg:hidden">Sawa Kart</span>
      <span className="flex-1" />
      <LiveStatus connected={connected} />
    </header>
  );
}
